import { useState, useEffect, useRef } from 'react'
import { Spinner } from '../ui/Spinner'

// Tenor API key — override with VITE_TENOR_KEY env var for production
const TENOR_KEY = import.meta.env.VITE_TENOR_KEY ?? 'AIzaSyAyimkuYQYF_FXVALexPzpStLHv7G25lmI'
const CLIENT_KEY = 'ggram'

interface TenorGif {
  id: string
  title: string
  media_formats: {
    gif?: { url: string; dims: [number, number] }
    tinygif?: { url: string; dims: [number, number] }
    nanogif?: { url: string; dims: [number, number] }
  }
}

interface Props {
  onSelect: (gifUrl: string) => void
  onClose: () => void
  inline?: boolean
}

const FEATURED_CATEGORIES = ['😂', '❤️', '👍', '🎉', '😮', '😢', '🔥', '✨']

export function GifPicker({ onSelect, onClose, inline }: Props) {
  const [query, setQuery] = useState('')
  const [gifs, setGifs] = useState<TenorGif[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const fetchGifs = async (q: string) => {
    setLoading(true)
    setError(false)
    try {
      const endpoint = q
        ? `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(q)}&key=${TENOR_KEY}&client_key=${CLIENT_KEY}&limit=24`
        : `https://tenor.googleapis.com/v2/featured?key=${TENOR_KEY}&client_key=${CLIENT_KEY}&limit=24`
      const res = await fetch(endpoint)
      if (!res.ok) throw new Error()
      const data = await res.json()
      setGifs(data.results ?? [])
    } catch {
      setError(true)
      setGifs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchGifs('')
  }, [])

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value
    setQuery(q)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => fetchGifs(q), 400)
  }

  const getUrl = (gif: TenorGif) =>
    gif.media_formats.tinygif?.url ?? gif.media_formats.nanogif?.url ?? gif.media_formats.gif?.url ?? ''

  return (
    <div className={`w-80 bg-bg-primary border border-divider overflow-hidden z-30 animate-fade-in
      ${inline ? 'rounded-b-2xl border-t-0' : 'absolute bottom-full left-0 mb-2 rounded-2xl shadow-2xl'}`}>
      {/* Search bar */}
      <div className="flex items-center gap-2 p-3 border-b border-divider">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
             className="text-txt-meta shrink-0" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          ref={inputRef}
          value={query}
          onChange={handleQueryChange}
          placeholder="Поиск GIF..."
          className="flex-1 bg-transparent text-txt-primary text-sm outline-none placeholder-txt-meta"
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
        />
        {loading && <Spinner size={12} />}
      </div>

      {/* Quick category chips */}
      {!query && (
        <div className="flex gap-1 px-3 pt-2 pb-1 flex-wrap">
          {FEATURED_CATEGORIES.map((emoji) => (
            <button
              key={emoji}
              onClick={() => { setQuery(emoji); fetchGifs(emoji) }}
              className="text-lg w-9 h-9 flex items-center justify-center rounded-xl hover:bg-bg-hover transition-colors"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className="h-56 overflow-y-auto p-2">
        {error && (
          <div className="flex items-center justify-center h-full text-txt-meta text-sm">
            Failed to load GIFs
          </div>
        )}
        {!error && !loading && gifs.length === 0 && (
          <div className="flex items-center justify-center h-full text-txt-meta text-sm">
            {query ? 'No GIFs found' : 'Trending GIFs loading...'}
          </div>
        )}
        {!error && (
          <div className="grid grid-cols-3 gap-1.5">
            {gifs.map((gif) => {
              const url = getUrl(gif)
              if (!url) return null
              return (
                <button
                  key={gif.id}
                  onClick={() => { onSelect(gif.media_formats.gif?.url ?? url); onClose() }}
                  className="rounded-lg overflow-hidden aspect-video bg-bg-input hover:opacity-80 transition-opacity"
                  title={gif.title}
                >
                  <img src={url} alt={gif.title} className="w-full h-full object-cover" loading="lazy" />
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div className="px-3 py-1.5 border-t border-divider flex items-center justify-between">
        <span className="text-txt-meta text-[10px]">Powered by Tenor</span>
        <button onClick={onClose} className="text-txt-meta hover:text-txt-primary text-xs">Close</button>
      </div>
    </div>
  )
}
